import { useEffect, useState } from "react";
import { GridRenderCellParams, useGridApiRef } from "@mui/x-data-grid-pro";

import { ListEventsRequest } from "shared/api/api";
import { getSignalEventsExport } from "shared/api/signalEvents/api";
import {
  useListSignalEventsOccurrences,
  useListSignalEventsOccurrencesCount,
} from "shared/api/signalEvents/hooks";
import { getSortFilter } from "shared/api/utils";
import { MAX_SIGNAL_EVENT_OCCURRENCES_DOWNLOAD_LIMIT } from "shared/constants";
import { useTenantMileageUnit } from "shared/hooks";
import { EventTypeEnum } from "shared/types";

import { DEFAULT_SIGNAL_EVENTS_SORT } from "pages/Dashboard/components/constants";
import SignalEventDetailsDrawer from "pages/Dashboard/components/EventDetails/SignalEventDetails/SignalEventDetails";
import EventsTableToolbar from "pages/Dashboard/components/EventsTableToolbar";
import useSignalEventOccurrencesViewSchema from "pages/Dashboard/components/signalEventsOccurenceSchema";
import VINLink from "pages/Dashboard/components/VINLink";
import { TableComponentProps } from "pages/Dashboard/types";
import { ROWS_PER_PAGE } from "pages/shared/Dashboard/constants";
import { SIGNAL_EVENTS_TABLE_PAGE_KEY } from "pages/SignalEventsAnalytics/constants";

import APIError from "features/ui/APIError";
import { FilterGroupState } from "features/ui/Filters/FilterBuilder/types";
import {
  getFiltersQuery,
  mergeFilterGroupStates,
} from "features/ui/Filters/FilterBuilder/utils";
import { useFilterSortState } from "features/ui/Filters/hooks";
import MaterialTable from "features/ui/MaterialTable";

const formatSignalEventListCellFunc =
  (renderCellParams: any) => (params: GridRenderCellParams) => {
    const { VIN } = params.row;

    switch (params.field) {
      case "VIN":
        return <VINLink config={renderCellParams.config} vin={VIN} />;
    }
  };

const SignalEventsTable = ({
  eventFilters,
  vehiclesFilters,
  onBadRequest,
  config,
  showCount = false,
  pageKey = SIGNAL_EVENTS_TABLE_PAGE_KEY,
  limit = ROWS_PER_PAGE,
  hideFooter = false,
  hideExport = false,
  onDataChange,
  smartGroups,
  onSmartGroupsChange,
  dashboardName,
  showUngroupedEvents,
  setShowUngroupedEvents,
  groupedCount,
  ungroupedCount,
  sharedTableFilterState,
  disablePersistence,
  customActions,
  downloadSource,
  onFiltersChange,
  defaultFilterValues,
  rowTestId,
  stickyHeader,
  stickyFooter,
  stickyTopOffset,
}: TableComponentProps) => {
  const [drawerIndex, setDrawerIndex] = useState<number | null>(null);
  const apiRef = useGridApiRef();
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const [pageIndex, setPageIndex] = useState<number>(0);
  const [signalEventIndex, setSignalEventIndex] = useState<number>(0);
  const { schema, attributeAccessors } = useSignalEventOccurrencesViewSchema(
    config.columnAccessors
  );

  // use a shared table filter state if provided, otherwise create local filter state
  const localFilterState = useFilterSortState({
    pageKey,
    defaultSort: DEFAULT_SIGNAL_EVENTS_SORT,
    schemaAttributes: attributeAccessors,
    disableUsingQuery: disablePersistence,
    disableUsingLocalStorage: disablePersistence,
  });

  const filterSortState = sharedTableFilterState || localFilterState;

  const {
    resetFilters,
    filters,
    initialized: filtersInitialized,
    sort,
    manageOnSortChange,
    resetFilterSortState,
    updateFilters,
  } = filterSortState;

  useEffect(() => {
    if (defaultFilterValues) {
      updateFilters(defaultFilterValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFilterValues]);

  const handleUpdateFilters = (updatedFilters: FilterGroupState) => {
    updateFilters(updatedFilters);
    if (onFiltersChange) {
      onFiltersChange(updatedFilters);
    }
  };

  const requestParams: ListEventsRequest = {
    sort: getSortFilter(sort),
    filter: getFiltersQuery(mergeFilterGroupStates(filters, eventFilters)),
    vehiclesFilter: vehiclesFilters && getFiltersQuery(vehiclesFilters),
    limit,
    mileageUnit: useTenantMileageUnit(),
    returnVehicles: true,
    returnSmartGroups: true,
  };

  const { data, isLoading, error, ...paginationData } =
    useListSignalEventsOccurrences(requestParams);

  const onNext = () => {
    if (drawerIndex == null) return;

    if (limit === drawerIndex + 1 && paginationData.hasNextPage) {
      paginationData.onNextPage();
      setDrawerIndex(0);
      setPageIndex((prev) => prev + 1);
    } else {
      setDrawerIndex(drawerIndex + 1);
    }

    setSignalEventIndex((prev) => prev + 1);
  };

  const onPrevious = () => {
    if (drawerIndex == null) return;

    if (drawerIndex === 0 && paginationData.hasPreviousPage) {
      paginationData.onPreviousPage();
      setDrawerIndex(limit - 1);
      setPageIndex((prev) => prev - 1);
    } else {
      setDrawerIndex(drawerIndex - 1);
    }

    setSignalEventIndex((prev) => prev - 1);
  };

  // add unique ID to each row to prevent duplicate key errors
  const dataWithUniqueIds = data?.map((row, index) => ({
    ...row,
    uniqueId: `${row.VIN}-${row.signalEventID}-${row.recordedAt}-${index}`,
  }));

  const downloadDisabled = !data || data.length === 0;

  const { data: countData } = useListSignalEventsOccurrencesCount({
    filter: requestParams.filter,
    vehiclesFilter: requestParams.vehiclesFilter,
  });

  const onRowClick = (index: number) => {
    setDrawerIndex(index);
    setSignalEventIndex(ROWS_PER_PAGE * (pageIndex ?? 0) + index + 1);
  };

  const handleFilterClick = () => {
    if (isFilterPanelOpen) {
      apiRef.current?.hideFilterPanel();
    } else {
      apiRef.current?.showFilterPanel();
    }
  };

  const activeFilterCount = filters
    ? Object.keys(filters.children || {}).length
    : 0;

  useEffect(() => {
    if (onDataChange && data) {
      onDataChange(data);
    }
  }, [data, onDataChange]);

  const toolbar = () => (
    <EventsTableToolbar
      smartGroups={smartGroups}
      onSmartGroupsChange={onSmartGroupsChange}
      groupedCount={groupedCount}
      ungroupedCount={ungroupedCount}
      showUngroupedEvents={showUngroupedEvents}
      setShowUngroupedEvents={setShowUngroupedEvents}
      smartGroupsAllowed={config.enableSmartGroups ?? false}
      dashboardName={dashboardName}
      isLoading={isLoading}
      showCount={showCount}
      countData={countData}
      customActions={customActions}
      eventType={EventTypeEnum.SIGNAL_EVENT}
      filters={{
        activeFilterCount,
        onClick: handleFilterClick,
        dataTestId: "signal-events-table-filter-button",
      }}
      download={
        !hideExport
          ? {
              disabled: downloadDisabled,
              downloadFunc: getSignalEventsExport,
              downloadSource,
              fileName: "signal-event-occurrences",
              requestParams: {
                ...requestParams,
                limit: MAX_SIGNAL_EVENT_OCCURRENCES_DOWNLOAD_LIMIT,
              },
              count: countData?.count as number,
              entityLabel: {
                key: `common:event_type.${EventTypeEnum.SIGNAL_EVENT.toLowerCase()}`,
              },
            }
          : undefined
      }
    />
  );

  if (error) {
    return (
      <APIError
        error={error}
        onBadRequest={() => {
          resetFilterSortState();
          onBadRequest();
        }}
      />
    );
  }

  return (
    <div className="relative">
      <MaterialTable
        schema={schema}
        data={dataWithUniqueIds}
        getRowId={(row) => row.uniqueId}
        {...paginationData}
        pageSize={limit}
        page={pageIndex}
        rowCount={countData?.count || 0}
        sort={sort}
        handleSorting={manageOnSortChange}
        formatCell={formatSignalEventListCellFunc({
          signalEventFilter: mergeFilterGroupStates(filters, eventFilters),
          onRowClick,
          config,
        })}
        loading={isLoading}
        filtersInitialized={filtersInitialized}
        onFiltersReset={resetFilters}
        filters={filters}
        updateFilters={handleUpdateFilters}
        hideFooter={hideFooter}
        onPageChange={(page) => setPageIndex(page)}
        toolbar={toolbar}
        apiRef={apiRef}
        onPreferencePanelOpen={() => setIsFilterPanelOpen(true)}
        onPreferencePanelClose={() => setIsFilterPanelOpen(false)}
        pageKey={pageKey}
        onRowClick={onRowClick}
        ignoreRowClickOnCells={["VIN"]}
        rowTestId={rowTestId}
        stickyHeader={stickyHeader}
        stickyFooter={stickyFooter}
        stickyTopOffset={stickyTopOffset}
      />

      <SignalEventDetailsDrawer
        signalEvent={dataWithUniqueIds?.find((_, i) => i === drawerIndex)}
        count={countData?.count ?? 0}
        signalEventIndex={signalEventIndex}
        onNext={onNext}
        onPrevious={onPrevious}
        openedIndex={drawerIndex}
        onClose={() => setDrawerIndex(null)}
        config={config}
        isLoading={isLoading}
      />
    </div>
  );
};

export default SignalEventsTable;
